/**
 * API client para os endpoints proxy de observabilidade do WhatsApi.
 *
 * Consome /api/v1/whatsapi-proxy/stats/* que retornam um envelope
 * { ok: boolean, data?: T, error?: string }. Falhas soft (resposta 200
 * com ok=false) sao convertidas em Error lancado pela queryFn.
 */

import { api } from './client'

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type Period = '1h' | '6h' | '24h' | '7d' | '30d'
export type Bucket = '5m' | '15m' | '1h' | '1d'

export interface Overview {
  period: Period
  generated_at: string
  sent: number
  failed: number
  queued: number
  deleted: number
  delivered?: number
  read?: number
  error_rate: number
  avg_latency_ms: number
  p95_latency_ms: number
  unique_targets: number
  active_api_keys: number
}

export interface ThroughputPoint {
  timestamp: string
  sent: number
  failed: number
  delivered?: number
  read?: number
  avg_latency_ms: number
}

export interface Throughput {
  period: Period
  bucket: Bucket
  generated_at: string
  points: ThroughputPoint[]
}

export interface ByApiKeyEntry {
  id: string | null
  name: string
  is_active: boolean
  sent: number
  failed: number
  delivered?: number
  read?: number
  avg_latency_ms: number
  last_used_at: string | null
}

export interface ByApiKey {
  period: Period
  generated_at: string
  keys: ByApiKeyEntry[]
}

export interface ErrorEntry {
  message: string
  count: number
  last_seen: string
}

export interface ErrorsResponse {
  period: Period
  generated_at: string
  errors: ErrorEntry[]
}

export interface Health {
  status: 'healthy' | 'degraded'
  database: 'connected' | 'disconnected'
  gateway: 'connected' | 'disconnected'
  checked_at: string
}

// ─── Envelope helper ─────────────────────────────────────────────────────────

interface Envelope<T> {
  ok: boolean
  data?: T
  error?: string
}

function unwrap<T>(payload: Envelope<T>): T {
  if (payload.ok === false) {
    throw new Error(payload.error || 'whatsapi proxy returned ok=false')
  }
  return payload.data as T
}

// ─── API Functions ───────────────────────────────────────────────────────────

const base = '/v1/whatsapi-proxy/stats'

export const whatsapiStatsApi = {
  overview: (period: Period): Promise<Overview> =>
    api.get<Envelope<Overview>>(`${base}/overview`, { params: { period } }).then((r) => unwrap(r.data)),

  throughput: (period: Period, bucket: Bucket): Promise<Throughput> =>
    api
      .get<Envelope<Throughput>>(`${base}/throughput`, { params: { period, bucket } })
      .then((r) => unwrap(r.data)),

  byApiKey: (period: Period): Promise<ByApiKey> =>
    api.get<Envelope<ByApiKey>>(`${base}/by-api-key`, { params: { period } }).then((r) => unwrap(r.data)),

  errors: (period: Period, limit = 20): Promise<ErrorsResponse> =>
    api
      .get<Envelope<ErrorsResponse>>(`${base}/errors`, { params: { period, limit } })
      .then((r) => unwrap(r.data)),

  health: (): Promise<Health> =>
    api.get<Envelope<Health>>(`${base}/health`).then((r) => unwrap(r.data)),
}

/** As quatro estatísticas que tenant, organização e global têm em comum. A saúde fica de
 *  fora: a organização não tem rota de saúde (spec 2026-10-23 §5.3). */
export interface EstatisticasWhatsApi {
  overview: (period: Period) => Promise<Overview>
  throughput: (period: Period, bucket: Bucket) => Promise<Throughput>
  byApiKey: (period: Period) => Promise<ByApiKey>
  errors: (period: Period, limit?: number) => Promise<ErrorsResponse>
}

/** Estatísticas sob `statsBase` (`/v1/tenants/{id}/whatsapi/stats` ou
 *  `/v1/organizations/{id}/whatsapi/stats`), todas no envelope `{ok, data}`. */
export function estatisticasEm(statsBase: string): EstatisticasWhatsApi {
  return {
    overview: (period) =>
      api.get<Envelope<Overview>>(`${statsBase}/overview`, { params: { period } })
        .then((r) => unwrap(r.data)),

    throughput: (period, bucket) =>
      api
        .get<Envelope<Throughput>>(`${statsBase}/throughput`, { params: { period, bucket } })
        .then((r) => unwrap(r.data)),

    byApiKey: (period) =>
      api.get<Envelope<ByApiKey>>(`${statsBase}/by-api-key`, { params: { period } })
        .then((r) => unwrap(r.data)),

    errors: (period, limit = 20) =>
      api
        .get<Envelope<ErrorsResponse>>(`${statsBase}/errors`, { params: { period, limit } })
        .then((r) => unwrap(r.data)),
  }
}

/**
 * Factory per-tenant: mesma shape do whatsapiStatsApi global, só muda base.
 * Backend resolve api_keys via whatsapi_keys.remote_id[] (client não envia keys).
 */
export function whatsapiTenantStatsApi(tenantId: number) {
  const tenantBase = `/v1/tenants/${tenantId}/whatsapi/stats`
  return {
    ...estatisticasEm(tenantBase),

    health: (): Promise<Health> =>
      api.get<Envelope<Health>>(`${tenantBase}/health`).then((r) => unwrap(r.data)),
  }
}
